<template>
  <div>
    <div v-if="displayType === 'long'">
      <p>
        Trailing mean returns and standard deviations presented in this analysis are hypothetical in nature. They do not reflect actual investment results and are not guarantees of future results.
      </p>
      <p>
        Trailing mean returns and standard deviations are presented as of {{ lastDayOfMonth }}. The chart displays month-end, annualized returns, assuming monthly portfolio rebalancing to the current allocation. Standard deviation is based off of historical returns data for each security in the portfolio. Returns data are adjusted for splits dividends, and assume reinvestment of dividends and capital gains. Returns data do not account for taxes, loads, sales charges, trading commissions, short-term trading fees, management fees, or advisory fees.
      </p>
      <p>
        Coverage includes Equities, Mutual Funds, ETFs, and Cash. 
        <span v-if="portfolio">
          Mean returns and standard deviation calculations cover
          {{ portfolio.trailing_returns_monthly_rebalance?.[2].toLocaleString('en-US', {style: 'percent'}) }}
          of {{ portfolioName }}.
        </span>
        <span v-if="benchmark">
          Mean returns and standard deviation calculations cover
          {{ benchmark.trailing_returns_monthly_rebalance?.[2].toLocaleString('en-US', {style: 'percent'}) }}
          of {{ benchmarkName }}.
        </span>
        <span v-if="optimizedPortfolio">
          Mean returns and standard deviation calculations cover
          {{ optimizedPortfolio.trailing_returns_monthly_rebalance?.[2].toLocaleString('en-US', {style: 'percent'}) }}
          of {{ optimizedPortfolioName }}.
        </span>
        Securities that do not have at least 3 years of price data are excluded from this chart.
      </p>
      <p>
        Standard deviation is calculated by taking the standard deviation of daily portfolio returns. Returns are calculated by multiplying the returns of each covered security in the portfolio by that security's current allocation in the portfolio using the following formula:
        Rp,n = Sum(Wi,n x Ri,n) from i=1 to m, where Rp,n is the portfolio returns at month n, Wi,n is the weight of asset i in month n, Ri,n is the monthly return of asset i in month n, and there are m securities with returns in month n. We assume Cash has 0 volatility for all monthly periods.
      </p>
      <p>
        When returns are not available for a holding (e.g. the security did not exist during historical period being reported), the remaining portfolio holdings are re-weighted to maintain consistent ratios using the following formula: Wnew,i,n = Wcurrent,i x (SUMcovered(Wcurrent,i)) / (1-(SUMnot_covered(Wcurrent,i))), where Wnew,i,n is the adjusted weight of security i at day n, SUMnot_covered is the sum over all securities that do not have daily returns available at day n, SUMcovered is the sum over all the securities that do have daily returns available at day n, and Wcurrent is the current weight of security i.
      </p>
      <p>
        Trailing Standard deviations are annualized by multiplying the standard deviation of daily portfolio returns by the square root of 252.
      </p>
      <p>
        Trailing mean returns are calculated by applying the following formula to monthly returns: Rp = (((1+Rp1) x (1+Rp2) x ... x (1+Rn))^1/Nmonths) - 1 where Rp is the total portfolio return, and Nmonths is the number of months in the period.
      </p>
      <p>
        Trailing mean returns are annualized using the following formula: Rp,annualized = ((1 + Rp)^(12/Nmonths)) - 1, where Rp is the total portfolio return, and Nmonths is the number of months in the period.
      </p>
      <p>
        Turnover is collected from a fund's annual report. Turnover is calculated by taking the lesser of purchases or sales (excluding all securities with maturities of less than one year) and dividing by average monthly net assets.
      </p>
      <p>
        Debt to Equity is defined as the company's total short and long term debt divided by its market capitalization.
        Forward PE Ratio is calculated by dividing the current stock price by the estimated earnings per share for the next twelve months.
        Debt to Equity and Forward PE Ratio are both calculated quarterly.
      </p>
      <p>
        The yield metric displayed is a composite of dividend yield, yield to maturity, and current yield, applied as follows based on the asset type.
      </p>
      <p>
        Dividend yield is used for equity securities. It is calculated as the annual dividends per share divided by the stock price. This metric is calculated quarterly and represents the percentage of a stock's price paid out as dividends.
      </p>
      <p>
        Yield to maturity is used for fixed income securities with a defined maturity date. It is calculated as the discount rate (YTM) that equates the present value of all future cash flows (coupon payments and face value) to the current market price of the bond. For money market funds, yield to maturity is sourced directly from SEC filings. For all other securities, the formula used is:
        P = (C * F / (1 + YTM)^1) + (C * F / (1 + YTM)^2) + ... + (C * F / (1 + YTM)^n) + (F / (1 + YTM)^n),
        where:
        P is the current market price of the bond,
        C is the coupon rate,
        F is the face value (par value), and
        n is the number of years to maturity.
        YTM is solved iteratively using the Newton-Raphson method.
      </p>
      <p>
        Current yield is used for bonds without a maturity date or when YTM cannot be reliably calculated (for example, for bonds near maturity or distressed assets). It is calculated as the annual coupon payment divided by the bond's market price. The annual coupon payment is calculated as the product of the coupon rate (C) and the bond's face value (F).
        The formula used is:
        Current Yield = (C * F) / P,
        where:
        C is the coupon rate,
        F is the face value (par value), and
        P is the current market price.
      </p>
      <p>
        The weighted average expense ratio is calculated at the portfolio level by multiplying the expense ratio of each holding by its percent weight in the portfolio, using the following formula: 
        Eavg = Sum(Wi x Ei), where Eavg is the weighted average expense ratio of the portfolio, Wi is the weight of asset i, and Ei is the expense ratio of asset i. Expense ratio data for each fund as of its most recent 485BPOS SEC filing.
      </p>
      <p>
        <span v-if="portfolio">
          Yield calculations cover
          {{ portfolio.sector_market_cap_asset_class_breakdown?.['yield_breakdown_coverage'].toLocaleString('en-US', {style: 'percent'}) }}
          of {{ portfolioName }}.
          <template v-if="portfolio.sector_market_cap_asset_class_breakdown">
            <template v-if="'equity_dividend_yield_breakdown_coverage' in portfolio.sector_market_cap_asset_class_breakdown">
              Dividend yield calculations cover
              {{
                portfolio.sector_market_cap_asset_class_breakdown['equity_dividend_yield_breakdown_coverage']
                  .toLocaleString('en-US', { style: 'percent' })
              }}
              of {{ portfolioName }}.
            </template>
            <template v-if="'fi_ytm_breakdown_coverage' in portfolio.sector_market_cap_asset_class_breakdown">
              Fixed Income YTM calculations cover
              {{
                portfolio.sector_market_cap_asset_class_breakdown['fi_ytm_breakdown_coverage']
                  .toLocaleString('en-US', { style: 'percent' })
              }}
              of {{ portfolioName }}.
            </template>
            <template v-if="'debt_to_equity_breakdown_coverage' in portfolio.sector_market_cap_asset_class_breakdown">
              Debt to Equity calculations cover
              {{
                portfolio.sector_market_cap_asset_class_breakdown['debt_to_equity_breakdown_coverage']
                  .toLocaleString('en-US', { style: 'percent' })
              }}
              of {{ portfolioName }}.
            </template>
            <template v-if="'forward_pe_breakdown_coverage' in portfolio.sector_market_cap_asset_class_breakdown">
              Forward PE Ratio calculations cover
              {{
                portfolio.sector_market_cap_asset_class_breakdown['forward_pe_breakdown_coverage']
                  .toLocaleString('en-US', { style: 'percent' })
              }}
              of {{ portfolioName }}.
            </template>
          </template>
        </span>
        <span v-if="benchmark">
          Yield calculations cover
          {{ benchmark.sector_market_cap_asset_class_breakdown?.['yield_breakdown_coverage'].toLocaleString('en-US', {style: 'percent'}) }}
          of {{ benchmarkName }}.
          <template v-if="benchmark.sector_market_cap_asset_class_breakdown">
            <template v-if="'equity_dividend_yield_breakdown_coverage' in benchmark.sector_market_cap_asset_class_breakdown">
              Dividend yield calculations cover
              {{
                benchmark.sector_market_cap_asset_class_breakdown['equity_dividend_yield_breakdown_coverage']
                  .toLocaleString('en-US', { style: 'percent' })
              }}
              of {{ benchmarkName }}.
            </template>
            <template v-if="'fi_ytm_breakdown_coverage' in benchmark.sector_market_cap_asset_class_breakdown">
              Fixed Income YTM calculations cover
              {{
                benchmark.sector_market_cap_asset_class_breakdown['fi_ytm_breakdown_coverage']
                  .toLocaleString('en-US', { style: 'percent' })
              }}
              of {{ benchmarkName }}.
            </template>
            <template v-if="'debt_to_equity_breakdown_coverage' in benchmark.sector_market_cap_asset_class_breakdown">
              Debt to Equity calculations cover
              {{
                benchmark.sector_market_cap_asset_class_breakdown['debt_to_equity_breakdown_coverage']
                  .toLocaleString('en-US', { style: 'percent' })
              }}
              of {{ benchmarkName }}.
            </template>
            <template v-if="'forward_pe_breakdown_coverage' in benchmark.sector_market_cap_asset_class_breakdown">
              Forward PE Ratio calculations cover
              {{
                benchmark.sector_market_cap_asset_class_breakdown['forward_pe_breakdown_coverage']
                  .toLocaleString('en-US', { style: 'percent' })
              }}
              of {{ benchmarkName }}.
            </template>
          </template>
        </span>
        <span v-if="optimizedPortfolio">
          Yield calculations cover
          {{ optimizedPortfolio.sector_market_cap_asset_class_breakdown?.['yield_breakdown_coverage'].toLocaleString('en-US', {style: 'percent'}) }}
          of {{ optimizedPortfolioName }}.
          <template v-if="optimizedPortfolio.sector_market_cap_asset_class_breakdown">
            <template v-if="'equity_dividend_yield_breakdown_coverage' in optimizedPortfolio.sector_market_cap_asset_class_breakdown">
              Dividend yield calculations cover
              {{
                optimizedPortfolio.sector_market_cap_asset_class_breakdown['equity_dividend_yield_breakdown_coverage']
                  .toLocaleString('en-US', { style: 'percent' })
              }}
              of {{ optimizedPortfolioName }}.
            </template>
            <template v-if="'fi_ytm_breakdown_coverage' in optimizedPortfolio.sector_market_cap_asset_class_breakdown">
              Fixed Income YTM calculations cover
              {{
                optimizedPortfolio.sector_market_cap_asset_class_breakdown['fi_ytm_breakdown_coverage']
                  .toLocaleString('en-US', { style: 'percent' })
              }}
              of {{ optimizedPortfolioName }}.
            </template>
            <template v-if="'debt_to_equity_breakdown_coverage' in optimizedPortfolio.sector_market_cap_asset_class_breakdown">
              Debt to Equity calculations cover
              {{
                optimizedPortfolio.sector_market_cap_asset_class_breakdown['debt_to_equity_breakdown_coverage']
                  .toLocaleString('en-US', { style: 'percent' })
              }}
              of {{ optimizedPortfolioName }}.
            </template>
            <template v-if="'forward_pe_breakdown_coverage' in optimizedPortfolio.sector_market_cap_asset_class_breakdown">
              Forward PE Ratio calculations cover
              {{
                optimizedPortfolio.sector_market_cap_asset_class_breakdown['forward_pe_breakdown_coverage']
                  .toLocaleString('en-US', { style: 'percent' })
              }}
              of {{ optimizedPortfolioName }}.
            </template>
          </template>
        </span>
      </p>
      <p>
        Custodial cash is excluded from yield calculations due to the variability in yield programs and interest rates offered by different financial institutions
      </p>
      <p>
        Average number of holdings for funds is a weighted average of number of holdings in each fund weighted by the dollars invested in the fund in the portfolio. Number of holdings is calculated recursively, meaning that we include the underlying holdings of a fund that is held by another fund. The as-of date of fund holdings data is the date of the fund's most recent NPORT SEC filing.
      </p>
    </div>

    <p v-else>
      Trailing mean returns and standard deviations are presented as of {{ lastDayOfMonth }}.
      Trailing mean returns are calculated using month-end, annualized returns, assuming monthly portfolio rebalancing to the current allocation.
      Standard deviation is based off of historical returns data for each security in the portfolio.
      Returns data are adjusted for splits dividends, and assume reinvestment of dividends and capital gains.
      Returns data do not account for taxes, loads, sales charges, trading commissions, short-term trading fees, management fees, or advisory fees.
      Yield is a composite of dividend yield, yield to maturity, and current yield, as described in the full methodology below.
      Debt to Equity is a company's total short and long term debt divided by its market capitalization. Forward PE Ratio is the current stock price divided by the estimated earnings per share for the next twelve months.
      Debt to Equity and Forward PE Ratio are both calculated quarterly.
      Turnover is collected from a fund's annual report.
      Average number of holdings for funds recursively looks at the holdings of funds held within funds.
      Coverage for each metric is displayed in the more detailed methodology below.
    </p>
  </div>
</template>


<script>
import { mapStores } from 'pinia';
import { useCheckupStore } from '@frontend/ui/stores/checkupStore';
export default {
  name: 'StatisticsMethodology',
  props: ['displayType', 'portfolio', 'benchmark', 'optimizedPortfolio'],
  computed: {
    ...mapStores(useCheckupStore),
    lastDayOfMonth() {
      const today = new Date();

      // 1. If there's a created date and it's in a different month/year than today, use it:
      if (this.checkupStore.portfolio_checkup_created_date) {
        const createdDate = new Date(this.checkupStore.portfolio_checkup_created_date);
        if (!isNaN(createdDate)) {
          // Only use createdDate if its month or year differs from today’s
          if (
            createdDate.getMonth() !== today.getMonth() ||
            createdDate.getFullYear() !== today.getFullYear()
          ) {
            // Move to the “zero‐th” day of the next month → last day of createdDate’s month
            const temp = new Date(createdDate);
            temp.setDate(0);

            const day = temp.getDate();
            const month = temp.getMonth() + 1;
            const year = temp.getFullYear();
            return `${month}/${day}/${year}`;
          }
        }
      }

      // 2. Otherwise, try to pull “1_yr_returns” from portfolio → benchmark → optimizedPortfolio:
      const oneYr =
        this.portfolio?.trailing_returns_monthly_rebalance?.[3]?.['1_yr_returns'] ||
        this.benchmark?.trailing_returns_monthly_rebalance?.[3]?.['1_yr_returns'] ||
        this.optimizedPortfolio?.trailing_returns_monthly_rebalance?.[3]?.['1_yr_returns'];

      if (oneYr && Object.keys(oneYr).length) {
        // Keys are in “YYYY-MM-DD” form; pick the latest and parse it:
        const dates = Object.keys(oneYr);
        const lastDateKey = dates.reduce((a, b) => (a > b ? a : b), dates[0]);
        const parsed = new Date(lastDateKey);
        const m = parsed.getMonth() + 1;
        const d = parsed.getDate();
        const y = parsed.getFullYear();
        return `${m}/${d}/${y}`;
      }

      // 3. If neither of the above yields a date, fall back to the last day of the previous month:
      today.setDate(0);
      const mm = today.getMonth() + 1;
      const dd = today.getDate();
      const yy = today.getFullYear();
      return `${mm}/${dd}/${yy}`;
    },
    portfolioName() {
      return this.portfolio?.name || 'Portfolio';
    },
    benchmarkName() {
      return this.benchmark?.name || 'Benchmark';
    },
    optimizedPortfolioName() {
      return this.optimizedPortfolio?.name || 'Optimized Portfolio';
    },
  },
};
</script>
